import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ReactComponent as GoBackButton } from '../../../assets/svg/GoBackButton.svg';
import { fileSystemUrl } from '../../../server/connections';
import MDEditor, { divider } from '@uiw/react-md-editor';
import AudioSolution from './AudioSolution';

function SolutionScreen({ solution, currentTopic, setCurrentMode, openSolution }: { solution: any; currentTopic: any; setCurrentMode: any; openSolution: any }) {
	const scrollViewRef = useRef<any>(null);
	const containerRef = useRef<any>(null);
	const [isDragging, setIsDragging] = useState(false);
	const [startX, setStartX] = useState(0);
	const [scrollLeft, setScrollLeft] = useState(0);

	const [soundStatus, setSoundStatus] = useState('idle');
	const [currentSoundTime, setCurrentSoundTime] = useState(0);
	const hasSound = solution.audio;

	const soundFileUrl = hasSound && `${fileSystemUrl}/${solution.audio.url}`;
	const soundPlayable = ['idle', 'paused', 'finished', 'canceled'].includes(soundStatus);
	const sound: any = useMemo(() => {
		if (!hasSound) return;

		return new Audio(soundFileUrl);
	}, [soundFileUrl, hasSound]);

	useEffect(() => {
		if (!sound) return;

		sound.addEventListener('ended', function (e: any) {
			setSoundStatus('finished');
		});
		sound.addEventListener('timeupdate', function (e: any) {
			setCurrentSoundTime(e.target.currentTime);
		});
	}, [sound]);

	useEffect(() => {
		return () => {
			if (!sound) return;

			sound.pause();
			sound.currentTime = 0;
			setSoundStatus('idle');
			setCurrentSoundTime(0);
		};
	}, [solution]);

	useEffect(() => {
		scrollViewRef?.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
	}, [solution]);

	const playSound = () => {
		sound.play();
		setSoundStatus('playing');
	};
	const pauseSound = () => {
		sound.pause();
		setSoundStatus('paused');
	};

	const handleClickAudioControl = () => (soundPlayable ? playSound() : pauseSound());

	const handleMouseDown = (event: any) => {
		setIsDragging(true);
		setStartX(event.clientX - containerRef.current.offsetLeft);
		setScrollLeft(containerRef.current.scrollLeft);
	};

	const handleMouseUp = () => {
		setIsDragging(false);
	};

	const handleMouseMove = (event: any) => {
		if (!isDragging) return;
		const x = event.clientX - containerRef.current.offsetLeft;
		const scrollX = x - startX;
		containerRef.current.scrollLeft = scrollLeft - scrollX;
	};

	return (
		<div className={`w-full px-5 pt-[10px] h-[calc(100%-54px)] overflow-auto scrollbar-display-none ${!!solution?.audio ? 'pb-[150px]' : ''}`}>
			<div className='flex flex-row justify-between'>
				<div className='cursor-pointer' onClick={() => setCurrentMode('topic')}>
					<GoBackButton />
				</div>
				<p className='font-bold text-[#02393E] text-[20px] leading-[26px] text-center'>{currentTopic?.title}</p>
				<div className='w-[24px] h-[24px]'></div>
			</div>
			<div className='block mt-[30px]'>
				<div
					ref={containerRef}
					className='flex overflow-y-hidden overflow-x-auto w-full pb-3 items-center horizontal-grey-small-scroll '
					onMouseDown={handleMouseDown}
					onMouseUp={handleMouseUp}
					onMouseMove={handleMouseMove}
				>
					{currentTopic?.solutions?.map((item: any, index: number) => (
						<div
							ref={solution.id === item.id ? scrollViewRef : undefined}
							className={`rounded-[77px] min-w-[136px] pointer text-[16px] leading-5 p-[10px] font-bold flex-1 flex items-center justify-center not-selectable text-nowrap   mr-[10px] ${
								solution.id === item.id ? 'bg-white text-darkenPurple' : 'bg-transparent hover:bg-greyLight text-mudPurple'
							}`}
							onClick={() => {
								if (solution.id !== item.id) openSolution(item.id);
							}}
							key={index}
						>
							Solution {index + 1}
						</div>
					))}
				</div>
			</div>
			<p className='mt-[20px] font-bold text-darkenPurple text-[24px] leading-[31px] text-center'>{solution?.title}</p>
			{!!solution?.image && (
				<div className='w-full mt-[35px]'>
					<img style={{ objectFit: 'contain', width: '100%' }} src={fileSystemUrl + '/' + solution.image?.url} alt='' />
				</div>
			)}
			<div className='mt-3'>
				{solution?.paragraphs?.map((paragraph: any, index: number) => (
					<div className='mb-5 border border-[#F2F6FF] p-5 rounded-[10px] ' key={index}>
						<div className={`flex flex-row gap-[10px] ${solution?.paragraphs?.length === index + 1 ? 'mb-0' : 'mb-5'}`}>
							<MDEditor.Markdown style={{ backgroundColor: 'transparent', color: '#02393E', fontSize: 16, fontFamily: 'Quicksand' }} className='preview-list' source={paragraph.text} />
						</div>
					</div>
				))}
			</div>
			{!!solution?.audio && <div></div>}
			{!!solution?.audio && <AudioSolution solution={solution} handleClickAudioControl={handleClickAudioControl} soundPlayable={soundPlayable} />}
		</div>
	);
}

export default SolutionScreen;
